<template>
  <div class="landing-wrapper">
    <v-container class="py-8 py-md-12" fluid>
      <v-row justify="center">
        <v-col cols="12" lg="10">
          <h1 class="text-h4 text-md-h3 mb-6 font-weight-bold">
            Administración de obras y bloqueos
          </h1>

          <v-alert v-if="error" type="error" class="mb-4" closable @click:close="error = ''">
            {{ error }}
          </v-alert>
          <v-alert v-if="success" type="success" class="mb-4" closable @click:close="success = ''">
            {{ success }}
          </v-alert>

          <v-tabs v-model="tab" color="primary" class="mb-6">
            <v-tab value="plays">Obras</v-tab>
            <v-tab value="blackouts">Bloqueos de fechas</v-tab>
          </v-tabs>

          <div v-if="loading" class="d-flex justify-center py-8">
            <v-progress-circular indeterminate color="primary" size="48" />
          </div>

          <v-window v-else v-model="tab">
            <!-- Plays -->
            <v-window-item value="plays">
              <div class="obras-toolbar">
                <button type="button" class="obras-new-btn" @click="goToNewPlay">
                  NUEVA OBRA
                </button>
              </div>

              <div class="obras-panel">
                <div class="obras-panel-header">
                  <div class="obras-avatar">
                    <img v-if="companyLogo" :src="companyLogo" alt="Logo compañía" />
                    <v-icon v-else size="34" color="#ffffff">mdi-drama-masks</v-icon>
                  </div>
                  <h2 class="obras-panel-title">MIS OBRAS</h2>
                </div>

                <div class="obras-panel-body">
                  <div class="obras-table-scroll">
                    <table class="obras-table">
                      <thead>
                        <tr>
                          <th>NOMBRE</th>
                          <th>ESTADO</th>
                          <th>FECHA DE LA FUNCIÓN</th>
                          <th class="obras-col-actions">ACCIONES</th>
                        </tr>
                      </thead>
                      <tbody>
                        <tr v-for="play in plays" :key="play.id">
                          <td class="obras-cell-name">{{ play.title }}</td>
                          <td>
                            <span :class="['obras-status', play.active ? 'is-active' : 'is-inactive']">
                              {{ play.active ? 'ACTIVA' : 'INACTIVA' }}
                            </span>
                          </td>
                          <td class="obras-cell-date">{{ formatSeason(play) }}</td>
                          <td>
                            <div class="obras-actions">
                              <button
                                type="button"
                                class="obras-icon-btn"
                                title="Gestionar fechas / bloqueos"
                                @click="manageDates(play)"
                              >
                                <v-icon size="20">mdi-calendar-blank-outline</v-icon>
                              </button>
                              <button
                                type="button"
                                class="obras-edit-btn"
                                @click="goToEditPlay(play)"
                              >
                                <v-icon size="18">mdi-pencil-outline</v-icon>
                                EDITAR
                              </button>
                            </div>
                          </td>
                        </tr>
                        <tr v-if="plays.length === 0">
                          <td colspan="4" class="obras-empty">
                            Aún no tienes obras registradas.
                          </td>
                        </tr>
                      </tbody>
                    </table>
                  </div>
                </div>
              </div>
            </v-window-item>

            <!-- Blackouts -->
            <v-window-item value="blackouts">
              <div v-if="!blackoutFormOpen" class="obras-toolbar">
                <button type="button" class="obras-new-btn" @click="openBlackoutForm()">
                  NUEVO BLOQUEO
                </button>
              </div>

              <div v-if="blackoutFormOpen" class="obra-form-panel mb-6">
                <div class="obra-form-header">
                  <v-icon size="22" color="#ffffff">mdi-calendar-remove-outline</v-icon>
                  <span>{{ editingBlackoutId ? 'EDITAR BLOQUEO' : 'NUEVO BLOQUEO' }}</span>
                </div>
                <div class="obra-form-body">
                  <v-row>
                    <v-col cols="12" md="6">
                      <v-select
                        v-model="blackoutForm.play"
                        :items="playOptions"
                        item-title="title"
                        item-value="id"
                        label="Obra (vacío = global, solo admin)"
                        clearable
                        variant="outlined"
                      />
                    </v-col>
                    <v-col cols="12" md="6">
                      <v-text-field v-model="blackoutForm.reason" label="Motivo" variant="outlined" />
                    </v-col>
                    <v-col cols="12" md="4">
                      <v-text-field v-model="blackoutForm.start_date" label="Desde" type="date" variant="outlined" />
                    </v-col>
                    <v-col cols="12" md="4">
                      <v-text-field v-model="blackoutForm.end_date" label="Hasta" type="date" variant="outlined" />
                    </v-col>
                    <v-col cols="12" md="4">
                      <v-select
                        v-model="blackoutForm.weekdays"
                        :items="weekdayOptions"
                        item-title="label"
                        item-value="value"
                        label="Días (vacío = todos)"
                        multiple
                        chips
                        variant="outlined"
                      />
                    </v-col>
                  </v-row>

                  <div class="obra-form-actions">
                    <button type="button" class="obra-cancel-btn" @click="closeBlackoutForm">
                      CANCELAR
                    </button>
                    <button type="button" class="obra-save-btn" :disabled="saving" @click="saveBlackout">
                      {{ saving ? 'GUARDANDO…' : 'GUARDAR' }}
                    </button>
                  </div>
                </div>
              </div>

              <div class="obras-panel">
                <div class="obras-panel-header">
                  <div class="obras-avatar">
                    <v-icon size="34" color="#ffffff">mdi-calendar-remove-outline</v-icon>
                  </div>
                  <h2 class="obras-panel-title">BLOQUEOS DE FECHAS</h2>
                </div>

                <div class="obras-panel-body">
                  <div class="obras-table-scroll">
                    <table class="obras-table">
                      <thead>
                        <tr>
                          <th>OBRA</th>
                          <th>MOTIVO</th>
                          <th>RANGO</th>
                          <th>DÍAS</th>
                          <th class="obras-col-actions">ACCIONES</th>
                        </tr>
                      </thead>
                      <tbody>
                        <tr v-for="b in blackouts" :key="b.id">
                          <td class="obras-cell-name">{{ b.play_title || 'Global' }}</td>
                          <td>{{ b.reason || 'Sin motivo' }}</td>
                          <td class="obras-cell-date">
                            {{ formatDate(b.start_date) }} - {{ formatDate(b.end_date) }}
                          </td>
                          <td>{{ weekdayLabels(b.weekdays) || 'Todos' }}</td>
                          <td>
                            <div class="obras-actions">
                              <button
                                type="button"
                                class="obras-edit-btn"
                                @click="openBlackoutForm(b)"
                              >
                                <v-icon size="18">mdi-pencil-outline</v-icon>
                                EDITAR
                              </button>
                              <button
                                type="button"
                                class="obras-icon-btn"
                                title="Eliminar bloqueo"
                                @click="deleteBlackout(b)"
                              >
                                <v-icon size="20">mdi-delete-outline</v-icon>
                              </button>
                            </div>
                          </td>
                        </tr>
                        <tr v-if="blackouts.length === 0">
                          <td colspan="5" class="obras-empty">
                            No hay bloqueos registrados.
                          </td>
                        </tr>
                      </tbody>
                    </table>
                  </div>
                </div>
              </div>
            </v-window-item>
          </v-window>
        </v-col>
      </v-row>
    </v-container>

    <teatro-footer />
  </div>
</template>

<script>
import {
  fetchPlaysAdmin,
  fetchBlackouts,
  createBlackout,
  updateBlackout,
  deleteBlackout,
  fetchCompanies,
} from "@/services/teatroApi";

const emptyBlackout = {
  play: null,
  reason: "",
  start_date: "",
  end_date: "",
  weekdays: [],
};

export default {
  name: "PlayManager",
  data() {
    return {
      tab: "plays",
      loading: true,
      saving: false,
      error: "",
      success: "",
      plays: [],
      blackouts: [],
      companies: [],
      blackoutFormOpen: false,
      editingBlackoutId: null,
      blackoutForm: { ...emptyBlackout },
      weekdayOptions: [
        { value: 0, label: "Lun" },
        { value: 1, label: "Mar" },
        { value: 2, label: "Mie" },
        { value: 3, label: "Jue" },
        { value: 4, label: "Vie" },
        { value: 5, label: "Sab" },
        { value: 6, label: "Dom" },
      ],
    };
  },
  computed: {
    playOptions() {
      return this.plays.map((p) => ({ id: p.id, title: p.title }));
    },
    companyLogo() {
      const c = this.companies && this.companies[0];
      return c && c.logo ? c.logo : "";
    },
  },
  async created() {
    await this.loadAuth();
    await this.load();
  },
  methods: {
    async loadAuth() {
      try {
        await this.$store.dispatch("fetchIsAuthenticated");
      } catch (e) {
        console.error(e);
      }
    },
    async load() {
      this.loading = true;
      this.error = "";
      try {
        const [plays, blackouts, companies] = await Promise.all([
          fetchPlaysAdmin(),
          fetchBlackouts(),
          fetchCompanies(),
        ]);
        this.plays = plays || [];
        this.blackouts = blackouts || [];
        this.companies = companies || [];
      } catch (e) {
        this.error = "No se pudieron cargar los datos.";
        console.error(e);
      } finally {
        this.loading = false;
      }
    },
    goToNewPlay() {
      window.location.href = "/admin-obras/nueva/";
    },
    goToEditPlay(play) {
      window.location.href = `/admin-obras/${play.id}/editar/`;
    },
    openBlackoutForm(blackout = null) {
      this.editingBlackoutId = blackout ? blackout.id : null;
      this.blackoutForm = blackout
        ? {
            play: blackout.play,
            reason: blackout.reason || "",
            start_date: blackout.start_date || "",
            end_date: blackout.end_date || "",
            weekdays: Array.isArray(blackout.weekdays) ? blackout.weekdays : [],
          }
        : { ...emptyBlackout };
      this.blackoutFormOpen = true;
    },
    closeBlackoutForm() {
      this.blackoutFormOpen = false;
      this.editingBlackoutId = null;
      this.blackoutForm = { ...emptyBlackout };
    },
    async saveBlackout() {
      this.saving = true;
      this.error = "";
      this.success = "";
      try {
        const payload = {
          play: this.blackoutForm.play,
          reason: this.blackoutForm.reason,
          start_date: this.blackoutForm.start_date,
          end_date: this.blackoutForm.end_date,
          weekdays: this.blackoutForm.weekdays || [],
        };
        if (this.editingBlackoutId) {
          await updateBlackout(this.editingBlackoutId, payload);
        } else {
          await createBlackout(payload);
        }
        this.success = "Bloqueo guardado correctamente.";
        this.closeBlackoutForm();
        await this.load();
      } catch (e) {
        this.error = e.response?.data?.detail || "No se pudo guardar el bloqueo.";
        console.error(e);
      } finally {
        this.saving = false;
      }
    },
    async deleteBlackout(blackout) {
      if (!confirm("¿Eliminar este bloqueo?")) return;
      try {
        await deleteBlackout(blackout.id);
        this.success = "Bloqueo eliminado.";
        await this.load();
      } catch (e) {
        this.error = "No se pudo eliminar el bloqueo.";
        console.error(e);
      }
    },
    formatDate(iso) {
      if (!iso) return "-";
      const [y, m, d] = String(iso).slice(0, 10).split("-");
      return y && m && d ? `${d}/${m}/${y}` : iso;
    },
    formatSeason(play) {
      // Parse the ISO date parts directly to avoid timezone shifts.
      const fmt = (iso) => {
        if (!iso) return null;
        const [year, month, day] = String(iso).slice(0, 10).split("-");
        return year && month && day ? `${day}/${month}/${year}` : null;
      };
      const start = fmt(play.start_date);
      const end = fmt(play.end_date);
      if (start && end) return `${start} - ${end}`;
      return start || end || "-";
    },
    manageDates(play) {
      // Jump to the blackouts tab with a new blackout pre-filled for this play.
      this.tab = "blackouts";
      this.openBlackoutForm({ id: null, play: play.id });
    },
    weekdayLabels(values) {
      if (!Array.isArray(values) || values.length === 0) return "";
      return values
        .map((v) => this.weekdayOptions.find((o) => o.value === v)?.label || v)
        .join(", ");
    },
  },
};
</script>

<style scoped>
.text-wrap {
  white-space: normal;
  line-height: 1.3;
}

/* --- "Mis obras" toolbar + panel ----------------------------------------- */
.obras-toolbar {
  max-width: 1080px;
  margin: 0 auto 18px;
  display: flex;
  justify-content: flex-end;
}

.obras-panel {
  max-width: 1080px;
  margin: 0 auto;
  background: rgba(8, 5, 5, 0.66);
  border: 1px solid rgba(255, 255, 255, 0.08);
  border-radius: 26px;
  overflow: hidden;
  box-shadow: 0 24px 60px rgba(0, 0, 0, 0.55);
}

.obras-panel-header {
  position: relative;
  display: flex;
  align-items: center;
  height: 74px;
  padding: 0 32px 0 122px;
  background: linear-gradient(90deg, #c8102e 0%, #a50d25 100%);
}

.obras-avatar {
  position: absolute;
  left: 26px;
  top: 50%;
  transform: translateY(-50%);
  width: 86px;
  height: 86px;
  border-radius: 50%;
  background: #0c0808;
  border: 3px solid #0c0808;
  display: flex;
  align-items: center;
  justify-content: center;
  overflow: hidden;
  box-shadow: 0 6px 18px rgba(0, 0, 0, 0.5);
}

.obras-avatar img {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.obras-panel-title {
  margin: 0;
  color: #fff;
  font-family: var(--font-family-heading);
  font-size: 15px;
  font-weight: 800;
  letter-spacing: 1.6px;
}

.obras-panel-body {
  padding: 28px 34px 30px;
}

.obras-table-scroll {
  overflow-x: auto;
}

.obras-table {
  width: 100%;
  border-collapse: collapse;
  color: var(--color-theater-text);
  font-family: var(--font-family-heading);
}

.obras-table th {
  text-align: left;
  padding: 8px 16px 18px;
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 1px;
  color: rgba(255, 255, 255, 0.55);
  white-space: nowrap;
}

.obras-table td {
  padding: 15px 16px;
  font-size: 13px;
  font-weight: 600;
  letter-spacing: 0.5px;
  vertical-align: middle;
  border-top: 1px solid rgba(255, 255, 255, 0.06);
}

.obras-table tbody tr:first-child td {
  border-top: none;
}

.obras-cell-name {
  text-transform: uppercase;
}

.obras-cell-date {
  white-space: nowrap;
  color: rgba(255, 255, 255, 0.9);
}

.obras-status {
  text-transform: uppercase;
  font-weight: 700;
}

.obras-status.is-active {
  color: #fff;
}

.obras-status.is-inactive {
  color: rgba(255, 255, 255, 0.55);
}

.obras-actions {
  display: flex;
  align-items: center;
  gap: 18px;
}

.obras-icon-btn,
.obras-edit-btn {
  background: transparent;
  border: none;
  color: var(--color-theater-text);
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  transition: opacity 0.2s ease, transform 0.2s ease;
}

.obras-icon-btn {
  padding: 4px;
}

.obras-edit-btn {
  gap: 8px;
  font-family: var(--font-family-heading);
  font-size: 12px;
  font-weight: 700;
  letter-spacing: 1px;
}

.obras-icon-btn:hover,
.obras-edit-btn:hover {
  opacity: 0.72;
  transform: translateY(-1px);
}

.obras-empty {
  text-align: center;
  padding: 30px 16px;
  color: rgba(255, 255, 255, 0.6);
  font-weight: 500;
  text-transform: none;
}

.obras-new-btn {
  background: linear-gradient(90deg, #c8102e 0%, #a50d25 100%);
  color: #fff;
  border: none;
  border-radius: 8px;
  padding: 12px 28px;
  font-family: var(--font-family-heading);
  font-size: 12px;
  font-weight: 800;
  letter-spacing: 1px;
  cursor: pointer;
  box-shadow: 0 6px 16px rgba(200, 16, 46, 0.35);
  transition: transform 0.2s ease, box-shadow 0.2s ease;
}

.obras-new-btn:hover {
  transform: translateY(-1px);
  box-shadow: 0 10px 22px rgba(200, 16, 46, 0.45);
}

@media (max-width: 600px) {
  .obras-panel-header {
    padding-left: 96px;
  }

  .obras-avatar {
    width: 64px;
    height: 64px;
    left: 18px;
  }

  .obras-panel-body {
    padding: 20px;
  }
}

/* --- Admin form panel (shared look with the play/company forms) ---------- */
.obra-form-panel {
  max-width: 1080px;
  margin: 0 auto 18px;
  background: rgba(8, 5, 5, 0.66);
  border: 1px solid rgba(255, 255, 255, 0.08);
  border-radius: 26px;
  overflow: hidden;
  box-shadow: 0 24px 60px rgba(0, 0, 0, 0.55);
}

.obra-form-header {
  display: flex;
  align-items: center;
  gap: 12px;
  height: 66px;
  padding: 0 32px;
  background: linear-gradient(90deg, #c8102e 0%, #a50d25 100%);
  color: #fff;
  font-family: var(--font-family-heading);
  font-size: 15px;
  font-weight: 800;
  letter-spacing: 1.4px;
}

.obra-form-body {
  padding: 30px 34px 28px;
}

.obra-form-actions {
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 26px;
  margin-top: 18px;
}

.obra-cancel-btn {
  background: transparent;
  border: none;
  color: var(--color-theater-text);
  cursor: pointer;
  font-family: var(--font-family-heading);
  font-size: 12px;
  font-weight: 700;
  letter-spacing: 1px;
  transition: opacity 0.2s ease;
}

.obra-cancel-btn:hover {
  opacity: 0.72;
}

.obra-save-btn {
  background: linear-gradient(90deg, #c8102e 0%, #a50d25 100%);
  color: #fff;
  border: none;
  border-radius: 8px;
  padding: 12px 34px;
  font-family: var(--font-family-heading);
  font-size: 12px;
  font-weight: 800;
  letter-spacing: 1px;
  cursor: pointer;
  box-shadow: 0 6px 16px rgba(200, 16, 46, 0.35);
  transition: transform 0.2s ease, box-shadow 0.2s ease;
}

.obra-save-btn:hover {
  transform: translateY(-1px);
  box-shadow: 0 10px 22px rgba(200, 16, 46, 0.45);
}

.obra-save-btn:disabled {
  opacity: 0.6;
  cursor: default;
  transform: none;
  box-shadow: none;
}

.obra-form-panel :deep(.v-field) {
  background: #ffffff !important;
  border-radius: 8px !important;
}

.obra-form-panel :deep(.v-field__outline) {
  --v-field-border-opacity: 0;
  color: transparent !important;
}

.obra-form-panel :deep(.v-field__input),
.obra-form-panel :deep(.v-field input),
.obra-form-panel :deep(.v-field textarea),
.obra-form-panel :deep(.v-select__selection-text) {
  color: #1a1a1a !important;
}

.obra-form-panel :deep(.v-field-label) {
  color: #7a7a7a !important;
  opacity: 1;
}

.obra-form-panel :deep(.v-field__append-inner .v-icon),
.obra-form-panel :deep(.v-field__clearable .v-icon) {
  color: #7a7a7a !important;
}

.obra-form-panel :deep(.v-chip) {
  background: #eceff1 !important;
  color: #1a1a1a !important;
}
</style>
